import { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import { trackEvent } from 'helpers/analytics';
import { activeNetworkSelector } from '../../../../redux/selectors';
import { useCreateSourcingCampaignMutation } from '../../../../services/findemV2';
import { useCreateSignupFormMutation } from '../../../../services/signupForms';
import { LIST_AGENT_CREDITS } from '../constants';

export const useListAgent = ({ sharedList }) => {
  const activeNetwork = useSelector(activeNetworkSelector);
  const availableCredits = activeNetwork?.availableAgentCredits || 0;
  const [isGrowListAgentOpen, setIsGrowListAgentOpen] = useState(false);
  const [createdCampaign, setCreatedCampaign] = useState(null);
  const [createSourcingCampaign, { isLoading: isCreatingCampaign }] = useCreateSourcingCampaignMutation();
  const [createSignupForm, { isLoading: isCreatingSignupForm }] = useCreateSignupFormMutation();

  const agentCampaign = useMemo(() => {
    const sourcingCampaign = sharedList?.sourcingCampaign;
    if (sourcingCampaign?.sharedListId) {
      return { ...sourcingCampaign, id: sourcingCampaign.campaignId };
    }
    return null;
  }, [sharedList]);

  const hasAgent = Boolean(agentCampaign);

  const handleSetupAgent = useCallback(async () => {
    trackEvent('contacts:list:setup_agent_click', {
      shared_list_id: sharedList?.id,
      list_name: sharedList?.name,
      collection_id: sharedList?.collection?.id,
    });

    try {
      await createSignupForm({
        collectionId: sharedList?.collection?.id,
        sharedListId: sharedList?.id,
      })
        .unwrap()
        .catch(() => {});

      const campaign = await createSourcingCampaign({
        sharedListId: sharedList?.id,
      }).unwrap();

      setCreatedCampaign(campaign);
      setIsGrowListAgentOpen(true);
    } catch (error) {
      toast.error('Failed to create campaign. Please try again.');
    }
  }, [sharedList, createSourcingCampaign, createSignupForm]);

  const handleCloseGrowListAgent = useCallback(() => {
    setIsGrowListAgentOpen(false);
    setCreatedCampaign(null);
  }, []);

  const handleOpenAgent = useCallback(() => {
    if (agentCampaign) {
      trackEvent('contacts:list:open_running_agent', {
        shared_list_id: sharedList?.id,
        list_name: sharedList?.name,
        collection_id: sharedList?.collection?.id,
        campaign_id: agentCampaign?.id,
      });
      setCreatedCampaign(sharedList?.sourcingCampaign);
      setIsGrowListAgentOpen(true);
    }
  }, [agentCampaign, sharedList]);

  return {
    isCreatingCampaign: isCreatingCampaign || isCreatingSignupForm,
    hasAgent,
    agentCampaign,
    availableCredits,
    hasEnoughCredits: availableCredits >= LIST_AGENT_CREDITS,
    handleSetupAgent,
    handleOpenAgent,
    isGrowListAgentOpen,
    handleCloseGrowListAgent,
    createdCampaign,
  };
};
